<script lang="ts">
	import { onMount } from 'svelte';
	import { Spinner } from 'flowbite-svelte';
	import { push } from '../../svelte-spa-router';
	import { getServiceDetail } from '../../AppNetwork';
	import type { ServiceDetail } from '../../AppTypes';
	import { statusLabel, statusText, statusBg } from '../../statusUtils';
	import UptimeBars from '../../components/public/UptimeBars.svelte';
	import ResponseTimeChart from '../../components/public/ResponseTimeChart.svelte';
	import IncidentCard from '../../components/public/IncidentCard.svelte';

	export let params: { slug?: string } = {};

	let detail: ServiceDetail | null = null;
	let loading = true;
	let error = '';

	async function load(slug: string) {
		loading = true;
		error = '';
		try {
			detail = await getServiceDetail(slug);
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to load service';
		} finally {
			loading = false;
		}
	}

	onMount(() => {
		if (params.slug) load(params.slug);
	});
</script>

<button class="text-sm text-gray-500 hover:underline mb-4" on:click={() => push('/')}>← Back to status</button>

{#if loading}
	<div class="flex justify-center py-20"><Spinner size="8" /></div>
{:else if error}
	<div class="text-center py-20 text-gray-500">{error}</div>
{:else if detail}
	<div class="space-y-8">
		<div class="flex items-center gap-3">
			<span class="inline-block w-3 h-3 rounded-full {statusBg(detail.service.status)}"></span>
			<h1 class="text-2xl font-bold text-gray-900 dark:text-white">{detail.service.name}</h1>
			<span class="text-sm font-medium {statusText(detail.service.status)}">{statusLabel(detail.service.status)}</span>
		</div>
		{#if detail.service.description}
			<p class="text-gray-500 dark:text-gray-400 -mt-4">{detail.service.description}</p>
		{/if}

		<section class="rounded-xl border border-gray-200 dark:border-zinc-700 p-5">
			<div class="flex items-center justify-between mb-3">
				<h2 class="font-semibold text-gray-900 dark:text-white">Uptime — last 90 days</h2>
				{#if detail.service.uptime90d !== null}
					<span class="text-sm text-gray-500">{detail.service.uptime90d}%</span>
				{/if}
			</div>
			<UptimeBars days={detail.days} />
		</section>

		<section class="rounded-xl border border-gray-200 dark:border-zinc-700 p-5">
			<h2 class="font-semibold text-gray-900 dark:text-white mb-3">Response time</h2>
			<ResponseTimeChart days={detail.days} />
		</section>

		{#if detail.incidents.length > 0}
			<section class="space-y-3">
				<h2 class="font-semibold text-gray-900 dark:text-white">Incident history</h2>
				{#each detail.incidents as incident}
					<IncidentCard {incident} />
				{/each}
			</section>
		{/if}
	</div>
{/if}
